import { AccountType } from './payment-method';

export interface BeneficiaryPaymentDetails {
  type: PaymentType;
  details: BankAccount;
}

export enum PaymentType {
  EFT = 'eft',
  CARD = 'card',
}

export interface BankAccount {
  bankName: Bank;
  branchCode: string;
  accountType: AccountType;
  accountNumber: string;
}

export interface DbBankAccount {
  bank_name: Bank;
  branch_code: string;
  account_type: AccountType;
  account_number: string;
}

export enum Bank {
  ABSA = 'absa',
  AfricanBank = 'african_bank',
  AfricanBankBusiness = 'african_bank_business',
  AlBaraka = 'al_baraka',
  BankOfLisbon = 'bank_of_lisbon',
  Bidvest = 'bidvest',
  Capitec = 'capitec',
  CapitecBusiness = 'capitec_business',
  Citibank = 'citibank',
  DiscoveryBank = 'discovery_bank',
  FNB = 'fnb',
  HabibOverseasBank = 'habib_overseas_bank',
  HBZ = 'hbz',
  Investec = 'investec',
  Ithala = 'ithala',
  JPMorganChase = 'jp_morgan_chase',
  Mercantile = 'mercantile',
  Nedbank = 'nedbank',
  OldMutual = 'old_mutual',
  Postbank = 'postbank',
  Sasfin = 'sasfin',
  SaReserveBank = 'sa_reserve_bank',
  StateBankOfIndia = 'state_bank_of_india',
  StandardBank = 'standard_bank',
  TymeBank = 'tyme_bank',
  Ubank = 'ubank',
  YWBNMutualBank = 'ywbn_mutual_bank',
  // United Kingdom Banks
  HSBCHoldings = 'hsbc_holdings',
  LloydsBankingGroup = 'lloyds_banking_group',
  RoyalBankOfScotlandGroup = 'royal_bank_of_scotland_group',
  Barclays = 'barclays',
  StandardChartered = 'standard_chartered',
  SantanderUK = 'santander_uk',
  NationwideBuildingSociety = 'nationwide_building_society',
  Schroders = 'schroders',
  CloseBrothersGroupPlc = 'close_brothers_group_plc',
  CoventryBuildingSociety = 'coventry_building_society',
}

export const BankInformation: {
  [key in Bank]: { name: string; branch?: string; key: Bank; isZA: boolean; isUK: boolean };
} & {
  [key: string]: { name: string; branch?: string; key: Bank | string; isZA: boolean; isUK: boolean };
} = {
  [Bank.ABSA]: { name: 'ABSA', branch: '632005', key: Bank.ABSA, isZA: true, isUK: false },
  [Bank.AfricanBank]: { name: 'African Bank', branch: '430000', key: Bank.AfricanBank, isZA: true, isUK: false },
  [Bank.AfricanBankBusiness]: {
    name: 'African Bank Business',
    branch: '584000',
    key: Bank.AfricanBankBusiness,
    isZA: true,
    isUK: false,
  },
  [Bank.AlBaraka]: { name: 'Al Baraka', branch: '800000', key: Bank.AlBaraka, isZA: true, isUK: false },
  [Bank.BankOfLisbon]: { name: 'Bank of Lisbon', branch: '450105', key: Bank.BankOfLisbon, isZA: true, isUK: false },
  [Bank.Bidvest]: { name: 'Bidvest', branch: '462005', key: Bank.Bidvest, isZA: true, isUK: false },
  [Bank.Capitec]: { name: 'Capitec', branch: '470010', key: Bank.Capitec, isZA: true, isUK: false },
  [Bank.Citibank]: { name: 'Citibank', branch: '350005', key: Bank.Citibank, isZA: true, isUK: false },
  [Bank.CapitecBusiness]: {
    name: 'Capitec Business',
    key: Bank.CapitecBusiness,
    branch: '450105',
    isZA: true,
    isUK: false,
  },
  [Bank.DiscoveryBank]: { name: 'Discovery Bank', branch: '679000', key: Bank.DiscoveryBank, isZA: true, isUK: false },
  [Bank.FNB]: { name: 'FNB', branch: '250655', key: Bank.FNB, isZA: true, isUK: false },
  [Bank.HabibOverseasBank]: {
    name: 'Habib Overseas Bank',
    branch: '701505',
    key: Bank.HabibOverseasBank,
    isZA: true,
    isUK: false,
  },
  [Bank.HBZ]: { name: 'HBZ', branch: '570100', key: Bank.HBZ, isZA: true, isUK: false },
  [Bank.Investec]: { name: 'Investec', branch: '580105', key: Bank.Investec, isZA: true, isUK: false },
  [Bank.Ithala]: { name: 'Ithala', branch: '754126', key: Bank.Ithala, isZA: true, isUK: false },
  [Bank.JPMorganChase]: {
    name: 'JP Morgan Chase',
    key: Bank.JPMorganChase,
    branch: '432000',
    isZA: false,
    isUK: false,
  },
  [Bank.Mercantile]: { name: 'Mercantile', branch: '450105', key: Bank.Mercantile, isZA: true, isUK: false },
  [Bank.Nedbank]: { name: 'Nedbank', branch: '198765', key: Bank.Nedbank, isZA: true, isUK: false },
  [Bank.OldMutual]: { name: 'Old Mutual', branch: '462005', key: Bank.OldMutual, isZA: true, isUK: false },
  [Bank.Postbank]: { name: 'Postbank', branch: '460005', key: Bank.Postbank, isZA: true, isUK: false },
  [Bank.Sasfin]: { name: 'Sasfin', branch: '683000', key: Bank.Sasfin, isZA: true, isUK: false },
  [Bank.SaReserveBank]: { name: 'SA Reserve Bank', branch: '900145', key: Bank.SaReserveBank, isZA: true, isUK: false },
  [Bank.StandardBank]: { name: 'Standard Bank', branch: '051001', key: Bank.StandardBank, isZA: true, isUK: false },
  [Bank.StateBankOfIndia]: {
    name: 'State Bank of India',
    branch: '801000',
    key: Bank.StateBankOfIndia,
    isZA: true,
    isUK: false,
  },
  [Bank.TymeBank]: { name: 'Tyme Bank', branch: '678910', key: Bank.TymeBank, isZA: true, isUK: false },
  [Bank.Ubank]: { name: 'Ubank', branch: '431010', key: Bank.Ubank, isZA: true, isUK: false },

  // Banks in both regions
  [Bank.HSBCHoldings]: { name: 'HSBC Holdings', branch: '587000', key: Bank.HSBCHoldings, isZA: true, isUK: true },

  // UK banks
  [Bank.LloydsBankingGroup]: { name: 'Lloyds Banking Group', key: Bank.LloydsBankingGroup, isZA: false, isUK: true },
  [Bank.RoyalBankOfScotlandGroup]: {
    name: 'Royal Bank of Scotland Group',
    key: Bank.RoyalBankOfScotlandGroup,
    isZA: false,
    isUK: true,
  },
  [Bank.Barclays]: { name: 'Barclays', key: Bank.Barclays, isZA: false, isUK: true },
  [Bank.StandardChartered]: {
    name: 'Standard Chartered',
    branch: '730020',
    key: Bank.StandardChartered,
    isZA: true,
    isUK: true,
  },
  [Bank.SantanderUK]: { name: 'Santander UK', key: Bank.SantanderUK, isZA: false, isUK: true },

  [Bank.NationwideBuildingSociety]: {
    name: 'Nationwide Building Society',
    key: Bank.NationwideBuildingSociety,
    isZA: false,
    isUK: true,
  },
  [Bank.Schroders]: { name: 'Schroders', key: Bank.Schroders, isZA: false, isUK: true },
  [Bank.CloseBrothersGroupPlc]: {
    name: 'Close Brothers Group Plc',
    key: Bank.CloseBrothersGroupPlc,
    isZA: false,
    isUK: true,
  },
  [Bank.CoventryBuildingSociety]: {
    name: 'Coventry Building Society',
    key: Bank.CoventryBuildingSociety,
    isZA: false,
    isUK: true,
  },
  [Bank.YWBNMutualBank]: {
    name: 'YWBN Mutual Bank',
    branch: '353000',
    key: Bank.YWBNMutualBank,
    isZA: true,
    isUK: false,
  },
};

export const prettyBankName = (bank: Bank): string => {
  return BankInformation[bank]?.name ?? bank;
};

export interface BankSelectOption {
  value: string;
  label: string;
}

export const getBanksByRegion = (region: 'ZA' | 'UK'): { [key: string]: any } => {
  const regionKey = region === 'ZA' ? 'isZA' : 'isUK';
  return Object.keys(BankInformation)
    .filter((key) => BankInformation[key][regionKey] === true)
    .reduce((result, key) => {
      result[key] = BankInformation[key];
      return result;
    }, {} as { [key: string]: any });
};

export const getBankSelectOptions = (region?: 'ZA' | 'UK'): BankSelectOption[] => {
  const banksToUse = region ? getBanksByRegion(region) : BankInformation;

  return Object.keys(banksToUse).map((key) => ({
    value: key,
    label: banksToUse[key].name,
  }));
};

export const getCurrentRegionBanks = (): { [key: string]: any } => {
  const region = process.env.REACT_APP_BANK_REGION === 'UK' ? 'UK' : 'ZA';
  return getBanksByRegion(region);
};

export const getCurrentRegionBankOptions = (): BankSelectOption[] => {
  const region = process.env.REACT_APP_BANK_REGION === 'UK' ? 'UK' : 'ZA';
  return getBankSelectOptions(region);
};
